import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import type { PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';
import {
  formatSelectedDayLabel,
  getBookingsForDay,
  sortBookingsByStart,
} from '@/calendar/pwaCalendarUtils';
import { BookingDetailSheet } from '@/components/BookingDetailSheet';
import { BookingListCard } from '@/components/BookingListCard';
import { ScreenBackButton } from '@/components/ScreenBackButton';

function parseDateParam(raw: string | undefined): Date | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

export function BookingsListPage() {
  const { date: dateParam } = useParams<{ date: string }>();
  const { get } = useSession();
  const dayDate = useMemo(() => parseDateParam(dateParam), [dateParam]);

  const [bookings, setBookings] = useState<PwaCalendarBooking[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlyConfirmed, setOnlyConfirmed] = useState(true);
  const [detailBooking, setDetailBooking] = useState<PwaCalendarBooking | null>(null);

  useEffect(() => {
    if (!dayDate) return;
    let cancelled = false;
    const boot = window.setTimeout(() => {
      void (async () => {
        setLoading(true);
        setLoadErr(null);
        try {
          const data = await get<{ bookings: PwaCalendarBooking[] }>(
            `/api/console/calendario/bookings?year=${dayDate.getFullYear()}&month=${dayDate.getMonth() + 1}`
          );
          if (!cancelled) setBookings(data.bookings);
        } catch (e) {
          if (!cancelled) {
            setBookings([]);
            setLoadErr(e instanceof ApiError ? e.message : 'No se pudieron cargar las reservas');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(boot);
    };
  }, [get, dayDate]);

  const dayBookings = useMemo(() => {
    if (!dayDate) return [];
    const forDay = [...getBookingsForDay(bookings, dayDate)].sort(sortBookingsByStart);
    if (!onlyConfirmed) return forDay;
    return forDay.filter((b) => b.status === 'confirmed');
  }, [bookings, dayDate, onlyConfirmed]);

  async function reloadMonth() {
    if (!dayDate) return;
    const data = await get<{ bookings: PwaCalendarBooking[] }>(
      `/api/console/calendario/bookings?year=${dayDate.getFullYear()}&month=${dayDate.getMonth() + 1}`
    );
    setBookings(data.bookings);
  }

  if (!dayDate) {
    return (
      <div className="bookings-list-page">
        <header className="bookings-list-page__header">
          <ScreenBackButton to="/calendar" />
          <h1 className="bookings-list-page__title">Reservas</h1>
        </header>
        <p className="pwa-err bookings-list-page__banner">Fecha inválida.</p>
      </div>
    );
  }

  return (
    <div className="bookings-list-page">
      <header className="bookings-list-page__header">
        <ScreenBackButton to="/calendar" />
        <h1 className="bookings-list-page__title">Reservas</h1>
        <label className="pwa-toggle bookings-list-page__toggle">
          <span className="pwa-toggle__label">Confirmadas</span>
          <span className="pwa-toggle__switch">
            <input
              type="checkbox"
              role="switch"
              className="pwa-toggle__input"
              checked={onlyConfirmed}
              onChange={(e) => setOnlyConfirmed(e.target.checked)}
              aria-label="Mostrar solo reservas confirmadas"
            />
            <span className="pwa-toggle__track" aria-hidden />
          </span>
        </label>
      </header>

      <p className="bookings-list-page__subtitle pwa-muted">{formatSelectedDayLabel(dayDate)}</p>

      {loadErr ? <p className="pwa-err bookings-list-page__banner">{loadErr}</p> : null}
      {loading ? <p className="pwa-muted bookings-list-page__banner">Cargando…</p> : null}

      <div className="bookings-list-page__scroll">
        {!loading && dayBookings.length === 0 ? (
          <p className="pwa-muted bookings-list-page__empty">
            {onlyConfirmed ? 'No hay reservas confirmadas este día.' : 'No hay reservas este día.'}
          </p>
        ) : (
          <div className="bookings-search-page__list">
            {dayBookings.map((b) => (
              <BookingListCard key={b.id} booking={b} onOpen={() => setDetailBooking(b)} />
            ))}
          </div>
        )}
      </div>

      {detailBooking ? (
        <BookingDetailSheet
          summary={detailBooking}
          onClose={() => setDetailBooking(null)}
          onChanged={() => {
            void reloadMonth();
            setDetailBooking(null);
          }}
        />
      ) : null}
    </div>
  );
}
